import {
  ENGINE_FAMILIES,
  FAMILY_PROCESS_TO_FLOW,
  FAMILY_PROFILE_TO_PERMISSION_SET,
  FAMILY_WORKFLOW_TO_FLOW,
  type EngineFamily,
} from "@/services/migrations/engine/types";

/**
 * How the three migration families are described on screen.
 *
 * THERE ARE THREE, AND THE THIRD IS NOT A VARIANT OF THE SECOND. `process_to_flow` shares
 * the WorkflowRule lane's TARGET half entirely — the same FlowIR, the same validator, the
 * same compiler — and none of its source half, and the difference is not cosmetic:
 *
 *   * A WorkflowRule has ONE entry condition. A Process Builder process has N ORDERED
 *     criteria nodes, each with its own actions, and a record that fails node 1 may still
 *     match node 2. So a Flow compiled from a process has EMPTY entry filters by
 *     construction and its coverage is graded per node against `process_contract`.
 *     Grading it against the rule contract would report every process as a total loss,
 *     which is why the engine has a third contract rather than a third shape in the second.
 *   * Its effect targets are NODE-SCOPED (`node:myDecision2|Account.Rating`) where the rule
 *     family's are bare, because the same field updated from two criteria nodes with two
 *     values is two behaviours, not one.
 *   * Its source metadata type IS `Flow` — a Process Builder process is a Flow whose
 *     `processType` is `Workflow`. Source and target are the same metadata type, which is a
 *     fact about Salesforce and not a mistake here.
 *   * AND IT HAS NO DECOMMISSION PATH. `decommission.py` deactivates a WorkflowRule by
 *     redeploying it with `<active>false</active>`, and that is the only source type it
 *     knows how to switch off. A process IS automation and DOES have an off switch — its
 *     FlowDefinition's active version is set to 0 — but this engine does not operate that
 *     one, so a process migration leaves its source running. Deliberately and visibly.
 *
 * The labels here are presentation. The identifiers are the wire's, and the server asserts
 * its own API list equals `executor.FAMILIES` so neither side can grow a fourth silently.
 */

export interface FamilyPresentation {
  family: EngineFamily;
  /** Short enough for a badge beside a plan title. */
  label: string;
  /** `<source> → <target>`, in the metadata types the engine actually reads and writes. */
  transition: string;
  /** One sentence a reader can act on. Never a restatement of the label. */
  detail: string;
  /**
   * Whether this family has a source automation the decommission phase can switch off.
   * `false` means a migration of it is finished when the replacement is applied and
   * verified, because there is nothing this engine will turn off afterwards.
   */
  hasDecommission: boolean;
  /**
   * Said on any family where `hasDecommission` is false AND the source is real automation
   * that keeps running. `null` where there is nothing to say — a Profile is not automation
   * and nobody expects it to be switched off.
   */
  sourceKeepsRunning: string | null;
}

const PRESENTATION: Record<EngineFamily, FamilyPresentation> = {
  [FAMILY_PROFILE_TO_PERMISSION_SET]: {
    family: FAMILY_PROFILE_TO_PERMISSION_SET,
    label: "Profile → Permission Set",
    transition: "Profile → PermissionSet",
    detail:
      "Compiles a Profile's grants into a Permission Set under a parity contract. A Profile is not automation, so nothing is switched off by this migration and nothing needs to be.",
    hasDecommission: false,
    sourceKeepsRunning: null,
  },
  [FAMILY_WORKFLOW_TO_FLOW]: {
    family: FAMILY_WORKFLOW_TO_FLOW,
    label: "Workflow Rule → Flow",
    transition: "WorkflowRule → Flow",
    detail:
      "Compiles one WorkflowRule — its entry criteria and its actions — into a record-triggered Flow. The only family whose source this engine can switch off afterwards.",
    hasDecommission: true,
    sourceKeepsRunning: null,
  },
  [FAMILY_PROCESS_TO_FLOW]: {
    family: FAMILY_PROCESS_TO_FLOW,
    label: "Process Builder → Flow",
    transition: "Flow (processType Workflow) → Flow",
    detail:
      "Compiles a Process Builder process into a record-triggered Flow. A process's criteria are an ordered sequence of decision nodes rather than one entry condition, so coverage is graded per node and the compiled Flow carries no entry filters by construction.",
    hasDecommission: false,
    // The one a reader of a "done" process migration has to be told, because the shape of
    // the finish line is different here and nothing else on the screen says so.
    sourceKeepsRunning:
      "This engine does not switch a Process Builder process off. A process is deactivated by setting its FlowDefinition's active version to 0, which is a different mechanism from the WorkflowRule redeploy this engine performs — so the source process keeps running until somebody deactivates it in Setup. Until then both automations exist on this object.",
  },
};

/** Every family, in the server's order — for a picker, a legend, or a filter. */
export const FAMILY_PRESENTATIONS: readonly FamilyPresentation[] = ENGINE_FAMILIES.map(
  (family) => PRESENTATION[family]
);

/**
 * The presentation for a family string off the wire, or `null` for one this client does
 * not know.
 *
 * `null` rather than a manufactured entry. A fourth family added server-side must render
 * as its own raw identifier — which is ugly, correct, and immediately obvious — rather
 * than as a guessed English label describing a lane nobody here has read.
 */
export function familyPresentation(family: string): FamilyPresentation | null {
  return PRESENTATION[family as EngineFamily] ?? null;
}

/** The label for a family, falling back to the raw wire string. Never to a guess. */
export function familyLabel(family: string): string {
  return familyPresentation(family)?.label ?? family;
}

/**
 * Does this family have a source automation the decommission phase can switch off?
 *
 * Answers `false` for an UNKNOWN family too, and that is the safe direction: it means a
 * screen offers no deactivation affordance for a lane this client has never heard of. The
 * report's own `decommission.applicable` is the authority per task; this is for the places
 * that have a family string and no task.
 */
export function familyHasDecommission(family: string): boolean {
  return familyPresentation(family)?.hasDecommission ?? false;
}
